import { areDevicesAnonymous } from '../audio/devices'
import { useTunerStore } from '../state/tuner-store'

interface DeviceSelectProps {
  readonly onSelect: (deviceId: string) => void
  /** Called when the user opens the picker, to pick up newly named devices. */
  readonly onOpen: () => void
}

/**
 * Audio input picker.
 *
 * Browsers withhold device names until microphone access has been granted
 * once, returning a single anonymous entry. Re-reading the list when the
 * picker is opened catches the case where access was granted in the meantime
 * without any `devicechange` event firing.
 */
export function DeviceSelect({ onSelect, onOpen }: DeviceSelectProps): React.JSX.Element {
  const devices = useTunerStore((state) => state.devices)
  const selectedDeviceId = useTunerStore((state) => state.selectedDeviceId)

  const hasDevices = devices.length > 0
  const isAnonymous = areDevicesAnonymous(devices)
  const value = devices.some((device) => device.deviceId === selectedDeviceId)
    ? (selectedDeviceId ?? '')
    : (devices[0]?.deviceId ?? '')

  return (
    // `--wide`: device names run long, and so does the hint below them — half
    // a phone's width would wrap either into a paragraph.
    <label className="controls__field controls__field--wide">
      <span>Eingang</span>
      <select
        value={value}
        disabled={!hasDevices}
        aria-label="Audioeingang"
        aria-describedby={isAnonymous ? 'device-hint' : undefined}
        onMouseDown={onOpen}
        onFocus={onOpen}
        onChange={(event) => {
          onSelect(event.target.value)
        }}
      >
        {hasDevices ? (
          devices.map((device) => (
            <option key={device.deviceId} value={device.deviceId}>
              {device.label}
            </option>
          ))
        ) : (
          <option value="">Kein Audioeingang gefunden</option>
        )}
      </select>
      {isAnonymous && (
        <small id="device-hint" className="controls__hint">
          Tuner einmal starten und den Mikrofonzugriff erlauben — danach erscheinen hier die
          Eingänge.
        </small>
      )}
    </label>
  )
}
